import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Autocomplete from "@mui/material/Autocomplete";
import Paper from "@mui/material/Paper";
import Box from "@mui/system/Box";
import AutocompleteTextField from "../AutocompleteTextField/AutoCompleteTextField";
import { LocationService } from "../../../services/LocationService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function CityAutocomplete({
  label,
  title,
  width,
  allowState,
  stateOnly,
  typeRequired,
  defaultInput,
  defaultSelected,
  defaultType,
  updateSelectedCity,
  updateType,
  disabled,
  error,
  disableCounterIncrement,
  disableAutoValue,
}) {
  const { instance } = useMsal();
  const [cityOptions, setCityOptions] = useState([]);
  const [defaultSearchInput, setDefaultSearchInput] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState("");
  const [type, setType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { warning } = useContext(AlertContext);

  const handleSetSelected = selectedOption => {
    if (selectedOption && type == "name" && !disableCounterIncrement) {
      setIsLoading(true);
      selectedOption.counter += 1;
      LocationService.UpdateCity(instance, selectedOption).then(res => {
        setSelected(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    } else {
      setSelected(selectedOption);
    }
  };

  const removeDuplicateCities = (res) => {
    let parsedCities;

    if (type === "name") {
      parsedCities = res.filter((value, index, self) =>
        index === self.findIndex((t) => (
          t.name === value.name && t.state === value.state
        ))
      );
    } else if (type === "zip") {
      parsedCities = res.filter((value, index, self) =>
        index === self.findIndex((t) => (
          t.zipCode === value.zipCode
        ))
      );
    }
    setCityOptions(parsedCities);

    if (defaultSearchInput && parsedCities.length === 1) {
      handleSetSelected(parsedCities[0]);
    } else if (!parsedCities.length && type !== "zip") {
      setSelected("");
    }
  };

  const setInputType = value => {
    if (value && value.length === 2 && !parseInt(value) && allowState) {
      setType("state");
    } else if (value && parseInt(value)) {
      setType("zip");
    } else (
      setType("name")
    );
  };

  useEffect(() => {
    setDefaultSearchInput(defaultInput);
    setSearchInput(defaultInput);
    setInputType(defaultInput);
  }, [defaultInput]);

  useEffect(() => {
    if (defaultSelected) {
      handleSetSelected(defaultSelected);
    }

    if (defaultType) {
      setType(defaultType);
    }
  }, [defaultSelected, defaultType]);

  useEffect(() => {
    updateSelectedCity(selected);
  }, [selected]);

  useEffect(() => {
    if (typeRequired) {
      updateType(type);
    }
  }, [type]);

  useEffect(() => {
    if (searchInput && searchInput.length > 3) {
      setIsLoading(true);

      const splitSearch = searchInput.split(",");

      let searchParams;

      if (type === "name") {
        searchParams = {
          name: splitSearch[0],
          state: splitSearch[1] && splitSearch[1].trim()
        };
      } else if (type === "zip") {
        searchParams = {
          zip: searchInput
        };
      }

      LocationService.FindCities(instance, searchParams).then(res => {
        removeDuplicateCities(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    } else if (type === "state" && searchInput.length === 2) {
      setIsLoading(true);

      const searchParams = {
        id: searchInput
      };

      LocationService.FindStates(instance, searchParams).then(res => {
        setCityOptions(res);
        if (defaultSearchInput && res.length === 1) {
          handleSetSelected(res[0]);
        }
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    } else {
      setCityOptions([]);
      setSelected("");
    }
  }, [searchInput]);

  const buildOptionLabel = option => {
    if (option && type === "zip") {
      return `${option.zipCode} - ${option.name}, ${option.state}`;
    } else if (option && type === "name") {
      return `${option.name}, ${option.state}`;
    } else if (type === "state") {
      return `${option.id}`;
    } else {
      return "";
    }
  };

  const buildListOption = option => {
    if (option && type === "zip") {
      return `${option.zipCode} - ${option.name}, ${option.state}`;
    } else if (option && type === "name") {
      return `${option.name}, ${option.state}`;
    } else if (type === "state") {
      return `${option.id} - ${option.name}`;
    } else {
      return "";
    }
  };

  return (
    <Autocomplete
      id={`autocomplete-${label}`}
      freeSolo
      autoHighlight
      disabled={disabled}
      value={!disableAutoValue && selected}
      onChange={(event, newValue) => {
        handleSetSelected(newValue);
      }}
      options={cityOptions}
      filterOptions={(x) => x}
      sx={{ 
        width: width || "300px",
        "& .MuiOutlinedInput-root": {
          padding: "1.5px 39px 1.5px 0 !important",
        },
      }}
      inputValue={searchInput ? searchInput : ""}
      PaperComponent={
        ({ children }) =>
          <Paper sx={{
            backgroundColor: "black_solid.10",
            fontSize: "body1.fontSize",
            fontWeight: "body1.fontWeight",
            boxShadow: "elevation1"
          }}
          >
            {children}
          </Paper>
      }
      onInputChange={(event, newInputValue) => {
        setDefaultSearchInput("");
        newInputValue !== "undefined" && setSearchInput(newInputValue);
        setInputType(newInputValue);
      }}
      onKeyDown={event => event.key === "Backspace" && setSelected("")}
      getOptionLabel={option => buildOptionLabel(option)}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {buildListOption(option)}
        </Box>
      )}
      renderInput={(params) =>
        <AutocompleteTextField
          label={label}
          title={title}
          isLoading={isLoading}
          searchInput={searchInput ? searchInput : ""}
          selected={selected}
          params={params}
          error={error}
          maxLength={stateOnly ? 2 : null}
        />
      }
    />
  );
}

CityAutocomplete.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.string,
  allowState: PropTypes.bool,
  stateOnly: PropTypes.bool,
  typeRequired: PropTypes.bool,
  defaultInput: PropTypes.string,
  defaultSelected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  defaultType: PropTypes.string,
  updateSelectedCity: PropTypes.func.isRequired,
  updateType: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  disableCounterIncrement: PropTypes.bool,
  disableAutoValue: PropTypes.bool,
};